import {
  OPERATOR_TYPES_1,
  OPERATOR_TYPES_3,
  OPERATOR_TYPES_4,
} from 'dashboard/routes/dashboard/settings/automation/operators';
import {
  DEFAULT_MESSAGE_CREATED_CONDITION,
  DEFAULT_CONVERSATION_CONDITION,
  DEFAULT_OTHER_CONDITION,
  DEFAULT_TRAVEL_CONDITION,
  DEFAULT_SUPPORT_CASE_CONDITION,
  DEFAULT_SUPPORT_CASE_ACTIONS,
  DEFAULT_ACTIONS,
} from 'dashboard/constants/automation';
import {
  TRAVEL_CONDITIONS,
  TRAVEL_EVENT_NAME,
  SUPPORT_CASE_EVENT_NAMES,
} from 'dashboard/routes/dashboard/settings/automation/constants';
import filterQueryGenerator from './filterQueryGenerator';
import actionQueryGenerator from './actionQueryGenerator';

/**
 * Removes the travel conditions from the builder unless the account has travel.
 *
 * They are absent rather than present-and-disabled: a disabled control invites someone to
 * go looking for the switch that enables it, and offering a condition that can never match
 * builds a rule the agency believes is covering them when it is not.
 *
 * Only the travel feature is checked, not an enabled GDS connection. Conditions read trip
 * state we already store, and a trip can exist without a connection, so a connection check
 * here would hide conditions that work. That check belongs to travel ACTIONS, which do call
 * the provider.
 * The travel event goes with them, rather than being left behind with an empty condition
 * list - an event whose only conditions are travel conditions has nothing to say once they
 * are gone.
 * @param {Object} automationTypes - The cloned AUTOMATIONS map.
 * @param {boolean} travelEnabled - Whether the account has the travel feature.
 * @returns {Object} The map, with travel conditions and the travel event removed when
 * travel is off.
 */
export const withTravelConditions = (automationTypes, travelEnabled) => {
  if (travelEnabled) return automationTypes;

  delete automationTypes[TRAVEL_EVENT_NAME];

  const travelKeys = TRAVEL_CONDITIONS.map(condition => condition.key);
  Object.values(automationTypes).forEach(automationType => {
    automationType.conditions = automationType.conditions.filter(
      condition => !travelKeys.includes(condition.key)
    );
  });

  return automationTypes;
};

/**
 * Removes the support case triggers from the builder unless the account has tickets.
 *
 * The twin of withTravelConditions, and absent-rather-than-disabled for the same reason: an
 * event that can never fire builds a rule the workspace believes is covering them.
 *
 * It removes the case events and nothing else, which is the whole of it - the case
 * conditions are offered on the case events only. They are deliberately not offered on the
 * conversation events: a conversation rule filtering on a case would be filtering on a case
 * the rule never chose.
 * @param {Object} automationTypes - The cloned AUTOMATIONS map.
 * @param {boolean} supportCasesEnabled - Whether the account has the support cases feature.
 * @returns {Object} The map, with the case events removed when tickets are off.
 */
export const withSupportCaseConditions = (
  automationTypes,
  supportCasesEnabled
) => {
  if (supportCasesEnabled) return automationTypes;

  SUPPORT_CASE_EVENT_NAMES.forEach(event => {
    delete automationTypes[event];
  });

  return automationTypes;
};

export const getCustomAttributeInputType = key => {
  const customAttributeMap = {
    date: 'date',
    text: 'plain_text',
    list: 'search_select',
    checkbox: 'search_select',
  };

  return customAttributeMap[key] || 'plain_text';
};

export const isACustomAttribute = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return attr.attribute_key === key;
  });
};

export const getCustomAttributeListDropdownValues = (
  customAttributes,
  type
) => {
  return customAttributes
    .find(attr => attr.attribute_key === type)
    .attribute_values.map(item => {
      return {
        id: item,
        name: item,
      };
    });
};

export const isCustomAttributeCheckbox = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === key && attr.attribute_display_type === 'checkbox'
    );
  });
};

export const isCustomAttributeList = (customAttributes, type) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === type && attr.attribute_display_type === 'list'
    );
  });
};

export const getOperatorTypes = key => {
  const operatorMap = {
    list: OPERATOR_TYPES_1,
    text: OPERATOR_TYPES_3,
    number: OPERATOR_TYPES_1,
    link: OPERATOR_TYPES_1,
    date: OPERATOR_TYPES_4,
    checkbox: OPERATOR_TYPES_1,
  };

  return operatorMap[key] || OPERATOR_TYPES_1;
};

export const generateCustomAttributeTypes = (customAttributes, type) => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      inputType: getCustomAttributeInputType(attr.attribute_display_type),
      filterOperators: getOperatorTypes(attr.attribute_display_type),
      customAttributeType: type,
    };
  });
};

export const generateConditionOptions = (options, key = 'id') => {
  if (!options || !Array.isArray(options)) return [];
  return options.map(i => {
    return {
      id: i[key],
      name: i.title,
    };
  });
};

export const getActionOptions = ({
  agents,
  teams,
  labels,
  slaPolicies,
  type,
  addNoneToListFn,
  priorityOptions,
  caseStageOptions,
}) => {
  const actionsMap = {
    assign_agent: addNoneToListFn ? addNoneToListFn(agents) : agents,
    assign_team: addNoneToListFn ? addNoneToListFn(teams) : teams,
    send_email_to_team: teams,
    add_label: generateConditionOptions(labels, 'title'),
    remove_label: generateConditionOptions(labels, 'title'),
    change_priority: priorityOptions,
    add_sla: slaPolicies,
    // A ticket stage is an account stage and a ticket owner is an account agent or team, so
    // the case actions read the same catalogues the case conditions read. Neither owner
    // action takes the "None" option the conversation ones do: a rule states the whole
    // owner, and `case_add_note` is free text with no list to offer at all.
    case_change_stage: caseStageOptions,
    case_assign_agent: agents,
    case_assign_team: teams,
  };
  return actionsMap[type];
};

export const getConditionOptions = ({
  agents,
  booleanFilterOptions,
  campaigns,
  contacts,
  countries,
  customAttributes,
  inboxes,
  languages,
  labels,
  statusFilterOptions,
  teams,
  type,
  priorityOptions,
  messageTypeOptions,
  travelTripStatusOptions,
  travelHoldStateOptions,
  caseTypeOptions,
  caseStageOptions,
  caseStageCategoryOptions,
  casePriorityOptions,
}) => {
  if (isCustomAttributeCheckbox(customAttributes, type)) {
    return booleanFilterOptions;
  }

  if (isCustomAttributeList(customAttributes, type)) {
    return getCustomAttributeListDropdownValues(customAttributes, type);
  }

  const conditionFilterMaps = {
    status: statusFilterOptions,
    assignee_id: agents,
    contact: contacts,
    inbox_id: inboxes,
    team_id: teams,
    campaigns: generateConditionOptions(campaigns),
    browser_language: languages,
    conversation_language: languages,
    country_code: countries,
    message_type: messageTypeOptions,
    private_note: booleanFilterOptions,
    priority: priorityOptions,
    labels: generateConditionOptions(labels, 'title'),
    travel_trip_status: travelTripStatusOptions,
    travel_hold_state: travelHoldStateOptions,
    travel_schedule_changed: booleanFilterOptions,
    travel_provider_status_stale: booleanFilterOptions,
    case_type_id: caseTypeOptions,
    case_stage_id: caseStageOptions,
    case_stage_category: caseStageCategoryOptions,
    case_priority: casePriorityOptions,
    // A case owner is an account agent and a case team is an account team, so both read
    // the same catalogues a conversation rule reads.
    case_assignee_id: agents,
    case_assignee_team_id: teams,
  };

  return conditionFilterMaps[type];
};

export const getFileName = (action, files = []) => {
  const blobId = action.action_params[0];
  if (!blobId) return '';
  if (action.action_name === 'send_attachment') {
    const file = files.find(item => item.blob_id === blobId);
    if (file) return file.filename.toString();
  }
  return '';
};

export const getDefaultConditions = eventName => {
  if (eventName === 'message_created') {
    return structuredClone(DEFAULT_MESSAGE_CREATED_CONDITION);
  }
  // The travel event offers travel conditions only, so the usual `status` default is not
  // one of its options - a rule opening on an attribute absent from its own list is broken
  // before the agent has touched it.
  if (eventName === TRAVEL_EVENT_NAME) {
    return structuredClone(DEFAULT_TRAVEL_CONDITION);
  }
  // Same reasoning: a case event offers the case conditions only, so the usual `status`
  // default is not one of its options.
  if (SUPPORT_CASE_EVENT_NAMES.includes(eventName)) {
    return structuredClone(DEFAULT_SUPPORT_CASE_CONDITION);
  }
  if (
    eventName === 'conversation_opened' ||
    eventName === 'conversation_resolved'
  ) {
    return structuredClone(DEFAULT_CONVERSATION_CONDITION);
  }
  return structuredClone(DEFAULT_OTHER_CONDITION);
};

export const getDefaultActions = eventName => {
  // The generic default assigns an agent to a conversation, which a case event neither
  // offers nor has - a rule opening on an action absent from its own list is broken before
  // the admin has touched it.
  if (SUPPORT_CASE_EVENT_NAMES.includes(eventName)) {
    return structuredClone(DEFAULT_SUPPORT_CASE_ACTIONS);
  }
  return structuredClone(DEFAULT_ACTIONS);
};

export const filterCustomAttributes = customAttributes => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      type: attr.attribute_display_type,
    };
  });
};

export const getStandardAttributeInputType = (automationTypes, event, key) => {
  return automationTypes[event].conditions.find(item => item.key === key)
    .inputType;
};

export const generateAutomationPayload = payload => {
  const automation = JSON.parse(JSON.stringify(payload));

  // A conditionless rule ("alert me on ANY SLA breach") has no last condition to null the
  // trailing query_operator on. It still ships the key, explicitly empty, rather than
  // relying on the API's tolerance of an omitted one.
  if (automation.conditions.length) {
    automation.conditions[automation.conditions.length - 1].query_operator =
      null;
    automation.conditions = filterQueryGenerator(automation.conditions).payload;
  }

  automation.actions = actionQueryGenerator(automation.actions);
  return automation;
};

export const isCustomAttribute = (attrs, key) => {
  return attrs.find(attr => attr.key === key);
};

export const generateCustomAttributes = (
  // eslint-disable-next-line default-param-last
  conversationAttributes = [],
  // eslint-disable-next-line default-param-last
  contactAttributes = [],
  conversationlabel,
  contactlabel
) => {
  const customAttributes = [];
  if (conversationAttributes.length) {
    customAttributes.push(
      {
        key: `conversation_custom_attribute`,
        name: conversationlabel,
        disabled: true,
      },
      ...conversationAttributes
    );
  }
  if (contactAttributes.length) {
    customAttributes.push(
      {
        key: `contact_custom_attribute`,
        name: contactlabel,
        disabled: true,
      },
      ...contactAttributes
    );
  }
  return customAttributes;
};

/**
 * Get attributes for a given key from automation types.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {string} key - The key to get attributes for.
 * @returns {Array} Array of condition objects for the given key.
 */
export const getAttributes = (automationTypes, key) => {
  return automationTypes[key].conditions;
};

/**
 * Get the automation type for a given key.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the automation type for.
 * @returns {Object} The automation type object.
 */
export const getAutomationType = (automationTypes, automation, key) => {
  return automationTypes[automation.event_name].conditions.find(
    condition => condition.key === key
  );
};

/**
 * Get the input type for a given key.
 * @param {Array} allCustomAttributes - Array of all custom attributes.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the input type for.
 * @returns {string} The input type.
 */
export const getInputType = (
  allCustomAttributes,
  automationTypes,
  automation,
  key
) => {
  const customAttribute = isACustomAttribute(allCustomAttributes, key);
  if (customAttribute) {
    return getCustomAttributeInputType(customAttribute.attribute_display_type);
  }
  const type = getAutomationType(automationTypes, automation, key);
  return type.inputType;
};

/**
 * Get operators for a given key.
 * @param {Array} allCustomAttributes - Array of all custom attributes.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} mode - The mode ('edit' or other).
 * @param {string} key - The key to get operators for.
 * @returns {Array} Array of operators.
 */
export const getOperators = (
  allCustomAttributes,
  automationTypes,
  automation,
  mode,
  key
) => {
  if (mode === 'edit') {
    const customAttribute = isACustomAttribute(allCustomAttributes, key);
    if (customAttribute) {
      return getOperatorTypes(customAttribute.attribute_display_type);
    }
  }
  const type = getAutomationType(automationTypes, automation, key);
  return type.filterOperators;
};

/**
 * Get the custom attribute type for a given key.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the custom attribute type for.
 * @returns {string} The custom attribute type.
 */
export const getCustomAttributeType = (automationTypes, automation, key) => {
  return automationTypes[automation.event_name].conditions.find(
    i => i.key === key
  ).customAttributeType;
};

/**
 * Determine if an action input should be shown.
 * @param {Array} automationActionTypes - Array of automation action type objects.
 * @param {string} action - The action to check.
 * @returns {boolean} True if the action input should be shown, false otherwise.
 */
export const showActionInput = (automationActionTypes, action) => {
  if (action === 'send_email_to_team' || action === 'send_message')
    return false;
  const type = automationActionTypes.find(i => i.key === action).inputType;
  return !!type;
};
